/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useMemo } from "react";
import {
  TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip,
} from "@mui/material";
import { IJobGrade, JOB_GRADES } from "data/job_grades";
import { flattenStructure, loadStructure, StructureNode } from "../StructureEditor/treeUtils";
import styles from "./styles.module.scss";

const loadCustomGrades = (): IJobGrade[] => {
  try { return JSON.parse(localStorage.getItem("job_grades_custom_v1") || "[]"); }
  catch { return []; }
};

// ── Types ─────────────────────────────────────────────────────────

export type MemberStatus = "wpisany" | "aktywuje" | "aktywny" | "archiwalny";

export interface OrgNode {
  id: string;
  type: "unit" | "position";
  name: string;
  /** Skrót nazwy jednostki (np. „WO", „USC"). Dotyczy węzłów typu „unit". */
  shortName?: string;
  expanded: boolean;
  children: OrgNode[];
  personFullName?: string;
  email?: string;
  phone?: string;
  gradeId?: string;
  positionId?: string;
  unitStructureId?: string;
  supervisorId?: string;
  managerId?: string;
  memberStatus?: MemberStatus;
}

// ── Colors ────────────────────────────────────────────────────────

const UNIT_COLORS = [
  "#2e7d32", "#1565c0", "#e65100", "#6a1b9a",
  "#b71c1c", "#00838f", "#558b2f", "#4527a0",
  "#37474f", "#ad1457",
];

// ── Persistence ───────────────────────────────────────────────────

export const STORAGE_KEY = "org_chart_v5";
const STORAGE_KEY_V4 = "org_chart_v4";

export const defaultTree = (): OrgNode[] => [
  {
    id: "root",
    type: "unit",
    name: "Zarząd",
    expanded: true,
    children: [
      { id: "p-wojt",  type: "position", name: "Wójt Gminy",       expanded: false, children: [], personFullName: "Mirosław Kowalczyk",   gradeId: "g-18", memberStatus: "aktywny" },
      { id: "p-zwojt", type: "position", name: "Zastępca Wójta",   expanded: false, children: [], personFullName: "Dorota Wiśniewska",    gradeId: "g-17", memberStatus: "aktywny" },
      { id: "p-sekr",  type: "position", name: "Sekretarz Gminy",  expanded: false, children: [], personFullName: "Marek Zielański",      gradeId: "g-15", memberStatus: "aktywny" },
      { id: "p-skarb", type: "position", name: "Skarbnik Gminy",   expanded: false, children: [], personFullName: "Elżbieta Nowakowska",  gradeId: "g-16", memberStatus: "aktywny" },
      {
        id: "u-org", type: "unit", name: "Wydział Organizacyjny", expanded: true, supervisorId: "p-sekr",
        children: [
          { id: "p-org-1", type: "position", name: "Naczelnik Wydziału",             expanded: false, children: [], personFullName: "Katarzyna Dąbrowska", gradeId: "g-11", memberStatus: "aktywny" },
          { id: "p-org-2", type: "position", name: "Inspektor ds. kadr i płac",      expanded: false, children: [], personFullName: "Anna Kowalska",       gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-org-3", type: "position", name: "Inspektor ds. informatyki",      expanded: false, children: [], personFullName: "Tomasz Lewandowski",  gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-org-4", type: "position", name: "Podinspektor ds. archiwum",      expanded: false, children: [], personFullName: "Barbara Kamińska",    gradeId: "g-03", memberStatus: "aktywny" },
          { id: "p-org-5", type: "position", name: "Referent ds. obsługi biurowej",  expanded: false, children: [], personFullName: "Monika Wróbel",        gradeId: "g-01", memberStatus: "aktywny" },
        ],
      },
      {
        id: "u-fin", type: "unit", name: "Wydział Finansowy", expanded: true, supervisorId: "p-skarb",
        children: [
          { id: "p-fin-1", type: "position", name: "Gł. Księgowy / Z-ca Skarbnika",    expanded: false, children: [], personFullName: "Joanna Mazurek",     gradeId: "g-10", memberStatus: "aktywny" },
          { id: "p-fin-2", type: "position", name: "Inspektor ds. budżetu",             expanded: false, children: [], personFullName: "Piotr Szymański",    gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-fin-3", type: "position", name: "Inspektor ds. księgowości",         expanded: false, children: [], personFullName: "Małgorzata Woźniak", gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-fin-4", type: "position", name: "Podinspektor ds. podatków",         expanded: false, children: [], personFullName: "Łukasz Jankowski",   gradeId: "g-03", memberStatus: "aktywny" },
          { id: "p-fin-5", type: "position", name: "Referent ds. egzekucji należności", expanded: false, children: [], personFullName: "Agnieszka Michalak", gradeId: "g-02", memberStatus: "aktywny" },
        ],
      },
      {
        id: "u-praw", type: "unit", name: "Wydział Prawny i ZP", expanded: true, supervisorId: "p-zwojt",
        children: [
          { id: "p-praw-1", type: "position", name: "Radca prawny",                      expanded: false, children: [], personFullName: "Krzysztof Zając",   gradeId: "g-29", memberStatus: "aktywny" },
          { id: "p-praw-2", type: "position", name: "Inspektor ds. zamówień publ.",      expanded: false, children: [], personFullName: "Izabela Pawlak",    gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-praw-3", type: "position", name: "Podinspektor ds. obsługi prawnej",  expanded: false, children: [], personFullName: "Rafał Krawczyk",    gradeId: "g-03", memberStatus: "aktywny" },
        ],
      },
      {
        id: "u-inw", type: "unit", name: "Wydział Inwestycji", expanded: false, supervisorId: "p-zwojt",
        children: [
          { id: "p-inw-1", type: "position", name: "Naczelnik Wydziału",             expanded: false, children: [], personFullName: "Andrzej Grabowski",  gradeId: "g-11", memberStatus: "aktywny" },
          { id: "p-inw-2", type: "position", name: "Inspektor ds. inwestycji",       expanded: false, children: [], personFullName: "Sylwia Krajewska",   gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-inw-3", type: "position", name: "Inspektor ds. dróg gminnych",    expanded: false, children: [], personFullName: "Dariusz Wiśniewski", gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-inw-4", type: "position", name: "Inspektor ds. ochrony środow.",  expanded: false, children: [], personFullName: "Natalia Kaczmarek",  gradeId: "g-04", memberStatus: "aktywny" },
        ],
      },
      {
        id: "u-plan", type: "unit", name: "Wydział Zagospod. Przestrzennego", expanded: false, supervisorId: "p-wojt",
        children: [
          { id: "p-plan-1", type: "position", name: "Naczelnik Wydziału",             expanded: false, children: [], personFullName: "Beata Nowak",         gradeId: "g-11", memberStatus: "aktywny" },
          { id: "p-plan-2", type: "position", name: "Inspektor ds. plano. przestrz.", expanded: false, children: [], personFullName: "Marcin Ostrowski",    gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-plan-3", type: "position", name: "Inspektor ds. budownictwa",      expanded: false, children: [], personFullName: "Renata Tomaszewska",  gradeId: "g-04", memberStatus: "aktywny" },
        ],
      },
      {
        id: "u-usc", type: "unit", name: "Urząd Stanu Cywilnego", expanded: false, supervisorId: "p-sekr",
        children: [
          { id: "p-usc-1", type: "position", name: "Kierownik USC",           expanded: false, children: [], personFullName: "Halina Wojciechowska", gradeId: "g-09", memberStatus: "aktywny" },
          { id: "p-usc-2", type: "position", name: "Zastępca Kierownika USC", expanded: false, children: [], personFullName: "Ewa Adamczyk",         gradeId: "g-10", memberStatus: "aktywny" },
        ],
      },
      {
        id: "u-soc", type: "unit", name: "Referat Spraw Obywatelskich", expanded: false, supervisorId: "p-sekr",
        children: [
          { id: "p-soc-1", type: "position", name: "Kierownik Referatu",               expanded: false, children: [], personFullName: "Lidia Malinowska",  gradeId: "g-09", memberStatus: "aktywny" },
          { id: "p-soc-2", type: "position", name: "Inspektor ds. ewidencji ludności", expanded: false, children: [], personFullName: "Marta Wierzbicka",  gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-soc-3", type: "position", name: "Podinspektor ds. dowodów osob.",   expanded: false, children: [], personFullName: "Kamil Kowalski",    gradeId: "g-03", memberStatus: "aktywny" },
        ],
      },
      {
        id: "u-osw", type: "unit", name: "Wydział Oświaty i Kultury", expanded: false, supervisorId: "p-wojt",
        children: [
          { id: "p-osw-1", type: "position", name: "Naczelnik Wydziału",    expanded: false, children: [], personFullName: "Jolanta Brzezińska",  gradeId: "g-11", memberStatus: "aktywny" },
          { id: "p-osw-2", type: "position", name: "Inspektor ds. oświaty", expanded: false, children: [], personFullName: "Robert Pietrzak",     gradeId: "g-04", memberStatus: "aktywny" },
          { id: "p-osw-3", type: "position", name: "Inspektor ds. kultury", expanded: false, children: [], personFullName: "Magdalena Czarnecka", gradeId: "g-04", memberStatus: "aktywny" },
        ],
      },
      {
        id: "u-sam", type: "unit", name: "Stanowiska samodzielne", expanded: false,
        children: [
          { id: "p-sam-1", type: "position", name: "Audytor wewnętrzny",       expanded: false, children: [], personFullName: "Grzegorz Zawadzki",  gradeId: "g-08", memberStatus: "aktywny" },
          { id: "p-sam-2", type: "position", name: "Inspektor ochrony danych", expanded: false, children: [], personFullName: "Aleksandra Bąk",     gradeId: "g-07", memberStatus: "aktywny" },
          { id: "p-sam-3", type: "position", name: "Specjalista ds. BHP",      expanded: false, children: [], personFullName: "Sławomir Górski",    gradeId: "g-06", memberStatus: "aktywny" },
        ],
      },
    ],
  },
];

// ── Migration from v4 ─────────────────────────────────────────────

function migrateNode(n: Record<string, unknown>): OrgNode {
  const memberStatus: MemberStatus | undefined = (n.memberStatus as MemberStatus) ?? (
    n.type === "position" ? (n.archived ? "archiwalny" : "aktywny") : undefined
  );
  return {
    id: n.id as string,
    type: n.type as "unit" | "position",
    name: n.name as string,
    shortName: n.shortName as string | undefined,
    expanded: (n.expanded as boolean) ?? false,
    children: ((n.children as Record<string, unknown>[]) ?? []).map(migrateNode),
    personFullName: n.personFullName as string | undefined,
    email: n.email as string | undefined,
    gradeId: n.gradeId as string | undefined,
    supervisorId: n.supervisorId as string | undefined,
    managerId: n.managerId as string | undefined,
    memberStatus,
  };
}

export const loadTree = (): OrgNode[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);

    const raw4 = localStorage.getItem(STORAGE_KEY_V4);
    if (raw4) {
      const migrated = (JSON.parse(raw4) as Record<string, unknown>[]).map(migrateNode);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }

    return defaultTree();
  } catch { return defaultTree(); }
};

export const saveTree = (n: OrgNode[]) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(n)); } catch { /* ignore */ }
};

// ── Public helpers ────────────────────────────────────────────────

export type OrgPersonEntry = {
  id: string;
  /** Nazwa wyświetlana (stanowisko w getOrgChartPositions, imię+nazwisko w getOrgChartPeople). */
  name: string;
  position: string;
  unit: string;
  /** Pełne imię i nazwisko osoby; undefined gdy stanowisko nieobsadzone. */
  personFullName?: string;
  email?: string;
  phone?: string;
  gradeId?: string;
  unitStructureId?: string;
  positionId?: string;
  memberStatus: MemberStatus;
};

export function getOrgChartPeople(): OrgPersonEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const tree: OrgNode[] = raw ? JSON.parse(raw) : defaultTree();
    const result: OrgPersonEntry[] = [];

    const collect = (nodes: OrgNode[], unitName: string) => {
      for (const n of nodes) {
        if (n.type === "position" && n.personFullName) {
          result.push({
            id: n.id,
            name: n.personFullName,
            position: n.name,
            unit: unitName,
            email: n.email,
            phone: n.phone,
            gradeId: n.gradeId,
            memberStatus: n.memberStatus ?? "aktywny",
          });
        } else if (n.type === "unit") {
          collect(n.children, n.name);
        }
      }
    };

    collect(tree, "");
    return result;
  } catch { /* ignore */ return []; }
}

/** Zwraca WSZYSTKIE stanowiska ze struktury (niezależnie czy mają przypisaną osobę).
 *  `name` = nazwa stanowiska, `personFullName` = imię/nazwisko lub undefined gdy wakat.
 *  Używa iteracyjnego BFS – przeszukuje każdy węzeł drzewa bez wyjątku. */
export function getOrgChartPositions(): OrgPersonEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const tree: OrgNode[] = raw ? JSON.parse(raw) : defaultTree();
    const result: OrgPersonEntry[] = [];

    // Stos: { node, nazwa_jednostki_nadrzędnej }
    const stack: Array<{ node: OrgNode; unitName: string }> =
      tree.map((n) => ({ node: n, unitName: "" }));

    while (stack.length > 0) {
      const { node, unitName } = stack.pop()!;

      if (node.type === "position") {
        result.push({
          id: node.id,
          name: node.name,
          position: node.name,
          unit: unitName,
          personFullName: node.personFullName || undefined,
          email: node.email,
          phone: node.phone,
          gradeId: node.gradeId,
          unitStructureId: node.unitStructureId,
          positionId: node.positionId,
          memberStatus: node.memberStatus ?? "aktywny",
        });
        // Jeśli stanowisko ma dzieci (niestandardowa struktura), przeszukaj je
        // z zachowaniem tej samej nazwy jednostki
        for (const child of node.children ?? []) {
          stack.push({ node: child, unitName });
        }
      } else {
        // Węzeł jednostki — przeszukaj jej dzieci, przekazując jej nazwę
        for (const child of node.children ?? []) {
          stack.push({ node: child, unitName: node.name });
        }
      }
    }

    return result;
  } catch { return []; }
}

/**
 * Bezpośredni przełożony każdego stanowiska: kierownik (managerId) najbliższej jednostki
 * nadrzędnej, która ma kierownika innego niż samo stanowisko; gdy stanowisko jest
 * kierownikiem swojej jednostki — kierownik jednostki wyżej, a na końcu „Odpowiedzialny
 * (Zarząd)" (supervisorId). Mapa: id stanowiska → id stanowiska przełożonego.
 * Używane przy synchronizacji struktury do BE (silnik workflow: „Bezpośredni przełożony").
 */
export function getOrgChartSupervisorMap(): Map<string, string> {
  const result = new Map<string, string>();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const tree: OrgNode[] = raw ? JSON.parse(raw) : defaultTree();

    const walk = (nodes: OrgNode[], chain: OrgNode[]) => {
      for (const n of nodes) {
        if (n.type === "position") {
          let sup: string | undefined;
          for (let i = chain.length - 1; i >= 0 && !sup; i--) {
            const u = chain[i];
            if (u.managerId && u.managerId !== n.id) sup = u.managerId;
          }
          if (!sup) {
            for (let i = chain.length - 1; i >= 0 && !sup; i--) {
              const u = chain[i];
              if (u.supervisorId && u.supervisorId !== n.id) sup = u.supervisorId;
            }
          }
          if (sup) result.set(n.id, sup);
          if (n.children?.length) walk(n.children, chain);
        } else {
          walk(n.children ?? [], [...chain, n]);
        }
      }
    };
    walk(tree, []);
  } catch { /* ignore */ }
  return result;
}

export function getOrgChartUnits(): Array<{ id: string; name: string; depth: number }> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const tree: OrgNode[] = raw ? JSON.parse(raw) : defaultTree();
    const result: Array<{ id: string; name: string; depth: number }> = [];

    const collect = (nodes: OrgNode[], depth: number) => {
      for (const n of nodes) {
        if (n.type === "unit") {
          result.push({ id: n.id, name: n.name, depth });
          collect(n.children, depth + 1);
        }
      }
    };

    collect(tree, 0);
    return result;
  } catch { /* ignore */ return []; }
}

export function addPersonToOrgUnit(unitId: string, data: {
  personFullName: string;
  positionName: string;
  email?: string;
  phone?: string;
  gradeId?: string;
  memberStatus: MemberStatus;
}): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const tree: OrgNode[] = raw ? JSON.parse(raw) : defaultTree();
    const newId = `p-${Date.now()}` as string;
    const newNode: OrgNode = {
      id: newId,
      type: "position",
      name: data.positionName,
      expanded: false,
      children: [],
      personFullName: data.personFullName,
      email: data.email,
      phone: data.phone,
      gradeId: data.gradeId,
      memberStatus: data.memberStatus,
    };
    const updated = updateNode(tree, unitId, (u) => ({
      ...u,
      expanded: true,
      children: [...u.children, newNode],
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return newId;
  } catch { /* ignore */ return ""; }
}

export function setOrgPersonStatus(nodeId: string, status: MemberStatus): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const tree: OrgNode[] = JSON.parse(raw);
    const updated = updateNode(tree, nodeId, (n) => ({ ...n, memberStatus: status }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch { /* ignore */ }
}

// ── Tree helpers ──────────────────────────────────────────────────

let _id = Date.now();
const nextId = () => `n-${_id++}`;

export function updateNode(nodes: OrgNode[], id: string, fn: (n: OrgNode) => OrgNode): OrgNode[] {
  return nodes.map((n) => n.id === id ? fn(n) : { ...n, children: updateNode(n.children, id, fn) });
}

export function removeNode(nodes: OrgNode[], id: string): OrgNode[] {
  return nodes.filter((n) => n.id !== id).map((n) => ({ ...n, children: removeNode(n.children, id) }));
}

export function findNode(nodes: OrgNode[], id: string): OrgNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const f = findNode(n.children, id);
    if (f) return f;
  }
  return null;
}

function isDescendantOrSelf(nodes: OrgNode[], ancestorId: string, targetId: string): boolean {
  const ancestor = findNode(nodes, ancestorId);
  if (!ancestor) return false;
  if (ancestor.id === targetId) return true;
  const check = (children: OrgNode[]): boolean =>
    children.some((c) => c.id === targetId || check(c.children));
  return check(ancestor.children);
}

export function moveNodeFn(tree: OrgNode[], nodeId: string, targetId: string): OrgNode[] {
  let moved: OrgNode | null = null;
  const extract = (nodes: OrgNode[]): OrgNode[] =>
    nodes.reduce<OrgNode[]>((acc, n) => {
      if (n.id === nodeId) { moved = n; return acc; }
      return [...acc, { ...n, children: extract(n.children) }];
    }, []);
  const without = extract(tree);
  if (!moved) return tree;
  const node = moved;
  return updateNode(without, targetId, (u) => ({ ...u, children: [...u.children, node] }));
}

export function findParent(nodes: OrgNode[], childId: string): OrgNode | null {
  for (const n of nodes) {
    if (n.children.some((c) => c.id === childId)) return n;
    const found = findParent(n.children, childId);
    if (found) return found;
  }
  return null;
}

export function collectPeople(nodes: OrgNode[], out: { id: string; name: string; position: string }[] = []): { id: string; name: string; position: string }[] {
  for (const n of nodes) {
    if (n.type === "position" && n.personFullName) {
      out.push({ id: n.id, name: n.personFullName, position: n.name });
    }
    collectPeople(n.children, out);
  }
  return out;
}

export function collectUnits(nodes: OrgNode[], out: { id: string; name: string; depth: number }[] = [], depth = 0): { id: string; name: string; depth: number }[] {
  for (const n of nodes) {
    if (n.type === "unit") {
      out.push({ id: n.id, name: n.name, depth });
      collectUnits(n.children, out, depth + 1);
    }
  }
  return out;
}

// Module-level drag state (safe for synchronous HTML5 DnD)
let _dragging: string | null = null;

// ── Status dot (for positions with non-active/non-archived status) ─

function StatusDot({ status }: { status?: MemberStatus }) {
  if (!status || status === "aktywny" || status === "archiwalny") return null;
  const isWpisany = status === "wpisany";
  return (
    <Tooltip title={isWpisany ? "Wpisany – brak zaproszenia" : "Oczekuje na aktywację"}>
      <span
        className={styles.statusDot}
        style={{ background: isWpisany ? "#9e9e9e" : "#ff9800" }}
      />
    </Tooltip>
  );
}

// ── Board member card (horizontal standalone node) ────────────────

function BoardMemberCard({
  node, color, isSelected, onSelect, onRemove, onAddUnit, onDropUnit,
}: {
  node: OrgNode; color: string; isSelected: boolean;
  onSelect: (id: string) => void; onRemove: () => void;
  onAddUnit: () => void;
  onDropUnit: (targetId: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      className={`${styles.boardMemberCard} ${isSelected ? styles.boardMemberCardSelected : ""} ${dragOver ? styles.unitBoxDragOver : ""}`}
      draggable
      onDragStart={(e) => { e.stopPropagation(); _dragging = node.id; }}
      onDragEnd={() => { _dragging = null; setDragOver(false); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (_dragging !== node.id) setDragOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); onDropUnit(node.id); }}
      onClick={() => onSelect(node.id)}
    >
      <div className={styles.boardMemberHeader} style={{ background: color }}>
        <span className={styles.boardMemberRole}>{node.name}</span>
      </div>
      <div className={styles.boardMemberBody}>
        <span className={styles.boardMemberName}>{node.personFullName ?? "—"}</span>
        <StatusDot status={node.memberStatus} />
      </div>
      <div className={styles.unitFooter} style={{ background: color }}>
        <Tooltip title="Dodaj jednostkę podrzędną">
          <button className={styles.hBtn} onClick={(e) => { e.stopPropagation(); onAddUnit(); }}>+ jed.</button>
        </Tooltip>
        <Tooltip title="Usuń stanowisko">
          <button className={`${styles.hBtn} ${styles.hBtnDanger}`} onClick={(e) => { e.stopPropagation(); onRemove(); }}>✕</button>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Unit box ──────────────────────────────────────────────────────

function UnitBox({
  node, color, depth, selectedId, hidePositions,
  onSelect, onToggle, onAddPos, onAddUnit, onRemove, onDropUnit,
}: {
  node: OrgNode; color: string; depth: number; selectedId: string | null;
  hidePositions?: boolean;
  onSelect: (id: string) => void; onToggle: () => void;
  onAddPos: () => void; onAddUnit: () => void; onRemove: () => void;
  onDropUnit: (targetId: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const positions = node.children.filter((c) => c.type === "position" && c.memberStatus !== "archiwalny");
  const isSelected = selectedId === node.id;

  return (
    <div
      className={`${styles.unitBox} ${isSelected ? styles.unitBoxSelected : ""} ${dragOver ? styles.unitBoxDragOver : ""}`}
      draggable={depth > 0}
      onDragStart={(e) => { e.stopPropagation(); _dragging = node.id; }}
      onDragEnd={() => { _dragging = null; setDragOver(false); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (_dragging !== node.id) setDragOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); onDropUnit(node.id); }}
    >
      <div
        className={`${styles.unitHeader} ${depth > 0 ? styles.draggableHeader : ""}`}
        style={{ background: color }}
        onClick={() => { onSelect(node.id); onToggle(); }}
      >
        <span className={styles.unitTitle}>{node.name}</span>
        {!hidePositions && <span className={styles.posCount}>{positions.length}</span>}
      </div>

      {!hidePositions && node.expanded && (
        <div className={styles.unitBody}>
          {positions.length === 0 ? (
            <div className={styles.emptyPositions}>Brak stanowisk</div>
          ) : (
            positions.map((pos) => (
              <div
                key={pos.id}
                className={`${styles.posRow} ${selectedId === pos.id ? styles.posRowSelected : ""}`}
                draggable
                onDragStart={(e) => { e.stopPropagation(); _dragging = pos.id; }}
                onDragEnd={() => { _dragging = null; }}
                onClick={() => onSelect(pos.id)}
              >
                <span className={styles.posRowDragHandle}>⠿</span>
                <span className={styles.posRowContent}>
                  <span className={styles.posRowTitle}>{pos.personFullName ?? "—"}</span>
                  <span className={styles.posRowPerson}>{pos.name}</span>
                </span>
                <StatusDot status={pos.memberStatus} />
              </div>
            ))
          )}
        </div>
      )}

      {!hidePositions && (
        <div className={styles.unitFooter} style={{ background: color }}>
          <Tooltip title="Dodaj stanowisko">
            <button className={styles.hBtn} onClick={(e) => { e.stopPropagation(); onAddPos(); }}>+ os.</button>
          </Tooltip>
          <Tooltip title="Dodaj jednostkę podrzędną">
            <button className={styles.hBtn} onClick={(e) => { e.stopPropagation(); onAddUnit(); }}>+ jed.</button>
          </Tooltip>
          {depth > 0 && (
            <Tooltip title="Usuń jednostkę">
              <button className={`${styles.hBtn} ${styles.hBtnDanger}`} onClick={(e) => { e.stopPropagation(); onRemove(); }}>✕</button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}

// ── Recursive tree renderer ───────────────────────────────────────

function TreeNode({
  node, depth, colorIdx, selectedId, supervisors,
  onSelect, onUpdate, onRemove, onAddChild, onDropUnit,
}: {
  node: OrgNode; depth: number; colorIdx: number; selectedId: string | null;
  supervisors: OrgNode[];
  onSelect: (id: string) => void;
  onUpdate: (id: string, patch: Partial<OrgNode>) => void;
  onRemove: (id: string) => void;
  onAddChild: (parentId: string, type: "unit" | "position") => void;
  onDropUnit: (targetId: string) => void;
}) {
  const color = depth === 0 ? "#3d2e9e" : UNIT_COLORS[colorIdx % UNIT_COLORS.length];
  const childUnits = node.children.filter((c) => c.type === "unit");

  const isRoot = depth === 0;
  const boardMembers = isRoot ? node.children.filter((c) => c.type === "position") : [];

  return (
    <div className={styles.treeNode}>
      <UnitBox
        node={node}
        color={color}
        depth={depth}
        selectedId={selectedId}
        hidePositions={isRoot}
        onSelect={onSelect}
        onToggle={() => onUpdate(node.id, { expanded: !node.expanded })}
        onAddPos={() => onAddChild(node.id, "position")}
        onAddUnit={() => onAddChild(node.id, "unit")}
        onRemove={() => onRemove(node.id)}
        onDropUnit={onDropUnit}
      />

      {/* Board member row — horizontal cards directly below root box */}
      {isRoot && boardMembers.length > 0 && (
        <>
          <div className={styles.connLine} />
          <div className={styles.childRow}>
            {boardMembers.map((bm) => {
              const bmChildUnits = bm.children.filter((c) => c.type === "unit");
              return (
                <div key={bm.id} className={styles.childItem}>
                  <div className={styles.connLine} />
                  <div className={styles.treeNode}>
                    <BoardMemberCard
                      node={bm}
                      color={color}
                      isSelected={selectedId === bm.id}
                      onSelect={onSelect}
                      onRemove={() => onRemove(bm.id)}
                      onAddUnit={() => { onAddChild(bm.id, "unit"); onUpdate(bm.id, { expanded: true }); }}
                      onDropUnit={onDropUnit}
                    />
                    {bm.expanded && bmChildUnits.length > 0 && (
                      <>
                        <div className={styles.connLine} />
                        <div className={styles.childColumn}>
                          {bmChildUnits.map((child, i) => (
                            <div key={child.id} className={styles.treeNode}>
                              <div className={styles.connLine} />
                              <TreeNode
                                node={child}
                                depth={1}
                                colorIdx={i}
                                selectedId={selectedId}
                                supervisors={supervisors}
                                onSelect={onSelect}
                                onUpdate={onUpdate}
                                onRemove={onRemove}
                                onAddChild={onAddChild}
                                onDropUnit={onDropUnit}
                              />
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Department children row */}
      {node.expanded && childUnits.length > 0 && (
        <>
          <div className={styles.connLine} />
          <div className={styles.childRow}>
            {childUnits.map((child, i) => (
              <div key={child.id} className={styles.childItem}>
                <div className={styles.connLine} />
                <TreeNode
                  node={child}
                  depth={depth + 1}
                  colorIdx={i}
                  selectedId={selectedId}
                  supervisors={supervisors}
                  onSelect={onSelect}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                  onAddChild={onAddChild}
                  onDropUnit={onDropUnit}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Edit panel ────────────────────────────────────────────────────

export function EditPanel({
  node, supervisors, allPeople, onUpdate, onDelete, onInvite, onMove, onClose,
}: {
  node: OrgNode;
  supervisors: OrgNode[];
  allPeople: { id: string; name: string; position: string }[];
  onUpdate: (patch: Partial<OrgNode>) => void;
  onDelete: () => void;
  onInvite: () => void;
  onMove: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState({
    name: node.name,
    personFullName: node.personFullName ?? "",
    email: node.email ?? "",
    gradeId: node.gradeId ?? "",
    positionId: node.positionId ?? "",
    unitStructureId: node.unitStructureId ?? "",
  });

  useEffect(() => {
    setDraft({
      name: node.name,
      personFullName: node.personFullName ?? "",
      email: node.email ?? "",
      gradeId: node.gradeId ?? "",
      positionId: node.positionId ?? "",
      unitStructureId: node.unitStructureId ?? "",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  const isDirty =
    draft.name !== node.name ||
    draft.personFullName !== (node.personFullName ?? "") ||
    draft.email !== (node.email ?? "") ||
    draft.gradeId !== (node.gradeId ?? "") ||
    draft.positionId !== (node.positionId ?? "") ||
    draft.unitStructureId !== (node.unitStructureId ?? "");

  const handleSave = () => {
    onUpdate({
      name: draft.name,
      personFullName: draft.personFullName || undefined,
      email: draft.email || undefined,
      gradeId: draft.gradeId || undefined,
      positionId: draft.positionId || undefined,
      unitStructureId: draft.unitStructureId || undefined,
    });
  };

  const allCustomGrades = loadCustomGrades();
  const allGrades = [...JOB_GRADES, ...allCustomGrades];

  const urzedniczeGrades = allGrades.filter((g) => g.group === "Urzędnicze");
  const stanowiskoGrades = allGrades.filter(
    (g) => g.group === "Kierownicze" || g.group === "Organy JST",
  );
  const orgUnits = flattenStructure(loadStructure()).filter((u) => u.hasChildren);

  const draftGrade = urzedniczeGrades.find((g) => g.id === draft.gradeId);

  // Liście StructureEditor pod wybranym węzłem jednostki organizacyjnej
  const positionsForUnit: Array<{ id: string; name: string }> = useMemo(() => {
    if (!draft.unitStructureId) return [];

    const findNode = (nodes: StructureNode[], id: string): StructureNode | null => {
      for (const n of nodes) {
        if (n.id === id) return n;
        const found = findNode(n.children, id);
        if (found) return found;
      }
      return null;
    };

    const collectLeaves = (node: StructureNode): Array<{ id: string; name: string }> => {
      if (node.children.length === 0) return [{ id: node.id, name: node.name }];
      return node.children.flatMap(collectLeaves);
    };

    const parent = findNode(loadStructure(), draft.unitStructureId);
    if (!parent) return [];
    return parent.children.flatMap(collectLeaves);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.unitStructureId]);

  return (
    <div className={styles.editPanel}>
      <div className={styles.editPanelHeader}>
        <span className={styles.editPanelLabel}>
          {node.type === "unit" ? "Jednostka organizacyjna" : "Edycja stanowiska"}
        </span>
        <button className={styles.closeBtn} onClick={onClose}>✕</button>
      </div>

      <div className={styles.editPanelBody}>
        {node.type === "unit" ? (
          <div className={styles.unitEditSection}>
            <TextField
              label="Nazwa jednostki"
              value={node.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              variant="standard"
              fullWidth
            />

            <TextField
              label="Skrót nazwy"
              value={node.shortName ?? ""}
              onChange={(e) => onUpdate({ shortName: e.target.value || undefined })}
              variant="standard"
              fullWidth
              inputProps={{ maxLength: 32 }}
              helperText="Skrót nazwy jednostki używany na schemacie"
            />

            <FormControl variant="standard" fullWidth>
              <InputLabel>Kierownik</InputLabel>
              <Select
                value={node.managerId ?? ""}
                label="Kierownik"
                onChange={(e) => onUpdate({ managerId: e.target.value || undefined })}
              >
                <MenuItem value=""><em>Brak</em></MenuItem>
                {allPeople.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}{p.position ? ` – ${p.position}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {supervisors.length > 0 && (
              <FormControl variant="standard" fullWidth>
                <InputLabel>Odpowiedzialny (Zarząd)</InputLabel>
                <Select
                  value={node.supervisorId ?? ""}
                  label="Odpowiedzialny (Zarząd)"
                  onChange={(e) => onUpdate({ supervisorId: e.target.value || undefined })}
                >
                  <MenuItem value=""><em>Brak</em></MenuItem>
                  {supervisors.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}{s.personFullName ? ` – ${s.personFullName}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <button className={styles.dangerBtn} onClick={onDelete}>
              Usuń jednostkę
            </button>
          </div>
        ) : (
          <div className={styles.posCard}>
            <TextField
              label="Imię i Nazwisko"
              value={draft.personFullName}
              onChange={(e) => setDraft((d) => ({ ...d, personFullName: e.target.value }))}
              variant="standard"
              fullWidth
            />

            <TextField
              label="E-mail"
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              variant="standard"
              fullWidth
              inputProps={{ inputMode: "email" }}
            />

            <FormControl variant="standard" fullWidth>
              <InputLabel>Stopień służbowy</InputLabel>
              <Select
                value={draft.gradeId}
                label="Stopień służbowy"
                onChange={(e) => setDraft((d) => ({ ...d, gradeId: e.target.value }))}
                renderValue={() => draftGrade?.name ?? ""}
              >
                <MenuItem value=""><em>Brak</em></MenuItem>
                {urzedniczeGrades.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              options={orgUnits}
              value={orgUnits.find((u) => u.id === draft.unitStructureId) ?? null}
              onChange={(_, val) =>
                setDraft((d) => ({ ...d, unitStructureId: val?.id ?? "", positionId: "" }))
              }
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderOption={(props, opt) => (
                <li {...props} key={opt.id}>
                  {opt.depth > 0 && (
                    <span style={{ color: "#bdbdbd", marginRight: 4, flexShrink: 0 }}>
                      {"—".repeat(opt.depth)}{" "}
                    </span>
                  )}
                  {opt.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Jednostka organizacyjna" variant="standard" />
              )}
              noOptionsText="Brak wyników"
              clearText="Wyczyść"
              openText="Otwórz"
              fullWidth
            />

            {draft.unitStructureId && (
              <FormControl variant="standard" fullWidth>
                <InputLabel>Stanowisko</InputLabel>
                <Select
                  value={draft.positionId}
                  label="Stanowisko"
                  onChange={(e) => setDraft((d) => ({ ...d, positionId: e.target.value }))}
                >
                  <MenuItem value=""><em>Brak</em></MenuItem>
                  {positionsForUnit.length > 0 ? (
                    positionsForUnit.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))
                  ) : (
                    stanowiskoGrades.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        <span style={{ color: "#9e9e9e", fontSize: 11, marginRight: 4 }}>{g.group} ·</span>
                        {g.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {isDirty && (
              <button className={styles.saveBtn} onClick={handleSave}>
                Zapisz zmiany
              </button>
            )}

            <div className={styles.posCardActions}>
              <button
                className={styles.archiveBtn}
                onClick={() => { onUpdate({ memberStatus: "archiwalny" }); onClose(); }}
              >
                Przenieś do archiwum
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Move dialog ───────────────────────────────────────────────────

export function MoveDialog({
  nodeName, units, onMove, onClose,
}: {
  nodeName: string;
  units: { id: string; name: string; depth: number }[];
  onMove: (targetId: string) => void;
  onClose: () => void;
}) {
  const [targetId, setTargetId] = useState("");

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600 }}>
        Przenieś: {nodeName}
      </DialogTitle>
      <DialogContent sx={{ pt: "12px !important" }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Przenieś do jednostki</InputLabel>
          <Select
            value={targetId}
            label="Przenieś do jednostki"
            onChange={(e) => setTargetId(e.target.value)}
          >
            {units.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {"  ".repeat(u.depth)}{u.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Anuluj</Button>
        <Button
          onClick={() => onMove(targetId)}
          size="small"
          variant="contained"
          disabled={!targetId}
          sx={{ background: "#5d49c7", "&:hover": { background: "#4a3aaa" } }}
        >
          Przenieś
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────

interface OrgChartProps {
  onInvite: () => void;
}

const OrgChart = ({ onInvite }: OrgChartProps) => {
  const [tree, setTree] = useState<OrgNode[]>(loadTree);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [moveNodeId, setMoveNodeId] = useState<string | null>(null);

  const persist = (next: OrgNode[]) => { setTree(next); saveTree(next); };

  const handleUpdate = (id: string, patch: Partial<OrgNode>) =>
    persist(updateNode(tree, id, (n) => ({ ...n, ...patch })));

  const handleRemove = (id: string) => {
    persist(removeNode(tree, id));
    if (selectedId === id) setSelectedId(null);
  };

  const handleAddChild = (parentId: string, type: "unit" | "position") => {
    const newNode: OrgNode = {
      id: nextId(),
      type,
      name: type === "unit" ? "Nowa jednostka" : "Nowe stanowisko",
      expanded: false,
      children: [],
      memberStatus: type === "position" ? "aktywny" : undefined,
    };
    persist(updateNode(tree, parentId, (n) => ({
      ...n,
      expanded: true,
      children: [...n.children, newNode],
    })));
    setSelectedId(newNode.id);
  };

  const handleDropUnit = (targetId: string) => {
    const dragId = _dragging;
    _dragging = null;
    if (!dragId || dragId === targetId) return;
    if (isDescendantOrSelf(tree, dragId, targetId)) return;
    const moved = moveNodeFn(tree, dragId, targetId);
    persist(updateNode(moved, targetId, (n) => ({ ...n, expanded: true })));
  };

  const handleMovePosition = (targetId: string) => {
    if (!moveNodeId) return;
    persist(moveNodeFn(tree, moveNodeId, targetId));
    setMoveNodeId(null);
  };

  const selectedNode = selectedId ? findNode(tree, selectedId) : null;
  const allUnits = collectUnits(tree);
  const allPeople = collectPeople(tree);

  const supervisors = tree.length > 0 ? tree[0].children.filter((c) => c.type === "position") : [];

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <span className={styles.hint}>Kliknij element aby edytować · Przeciągnij jednostkę aby zmienić jej położenie</span>
        <button
          className={styles.resetBtn}
          onClick={() => { localStorage.removeItem(STORAGE_KEY); setTree(defaultTree()); setSelectedId(null); }}
        >
          Przywróć domyślną strukturę
        </button>
      </div>

      <div className={styles.layout}>
        <div className={styles.chartArea}>
          <div className={styles.chartInner}>
            {tree.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                depth={0}
                colorIdx={0}
                selectedId={selectedId}
                supervisors={supervisors}
                onSelect={setSelectedId}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
                onAddChild={handleAddChild}
                onDropUnit={handleDropUnit}
              />
            ))}
          </div>
        </div>

        {selectedNode ? (
          <EditPanel
            node={selectedNode}
            supervisors={supervisors}
            allPeople={allPeople}
            onUpdate={(patch) => handleUpdate(selectedId!, patch)}
            onDelete={() => handleRemove(selectedId!)}
            onInvite={() => { onInvite(); setSelectedId(null); }}
            onMove={() => setMoveNodeId(selectedId)}
            onClose={() => {
              if (selectedNode?.type === "position") {
                const parent = findParent(tree, selectedId!);
                if (parent) handleUpdate(parent.id, { expanded: false });
              }
              setSelectedId(null);
            }}
          />
        ) : null}
      </div>

      {moveNodeId && (
        <MoveDialog
          nodeName={findNode(tree, moveNodeId)?.name ?? ""}
          units={allUnits.filter((u) => u.id !== moveNodeId)}
          onMove={handleMovePosition}
          onClose={() => setMoveNodeId(null)}
        />
      )}
    </div>
  );
};

export default OrgChart;
